import { useEffect, useRef, useState } from 'react';

import { useStore } from '../store/store';
import { Modal } from './common';
import { useConfirm } from './confirm';

/**
 * Profile switcher.
 *
 * One profile per person. Programs, favourites, notes, voice history and the
 * skip list all belong to the profile — switching swaps the lot. Everything
 * lives on this phone.
 */
export function ProfileMenu() {
  const store = useStore();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [managing, setManaging] = useState(false);
  const [newName, setNewName] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const initials = store.profile.name.trim().slice(0, 1).toUpperCase() || '?';

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        className="icon-btn"
        aria-label={`Profile: ${store.profile.name}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{ gap: 7, display: 'flex', alignItems: 'center', padding: '0 12px' }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: 'var(--accent)',
            color: 'var(--on-accent)',
            display: 'grid',
            placeItems: 'center',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {initials}
        </span>
        <span style={{ fontSize: 15, fontWeight: 600 }}>{store.profile.name}</span>
        <span aria-hidden="true" className="faint">
          ▾
        </span>
      </button>

      {open ? (
        <div className="menu" style={{ left: 0, right: 'auto' }}>
          {store.profiles.map((p) => (
            <button
              key={p.id}
              aria-current={p.id === store.profile.id}
              onClick={() => {
                store.switchProfile(p.id);
                setOpen(false);
              }}
            >
              {p.id === store.profile.id ? '✓ ' : ''}
              {p.name}
            </button>
          ))}
          <button
            onClick={() => {
              setOpen(false);
              setManaging(true);
            }}
          >
            + Add or manage profiles
          </button>
        </div>
      ) : null}

      {managing ? (
        <Modal title="Profiles" onClose={() => setManaging(false)}>
          <p className="muted" style={{ marginTop: 0 }}>
            Each person gets their own programs, favourites and notes, kept on this phone.
          </p>

          <div className="stack">
            {store.profiles.map((p) => (
              <div className="row" key={p.id}>
                <input
                  type="text"
                  value={p.name}
                  aria-label={`Name for ${p.name}`}
                  onChange={(e) => store.renameProfile(p.id, e.target.value)}
                />
                <button
                  className="btn small danger"
                  disabled={store.profiles.length <= 1}
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Delete ${p.name}?`,
                      body: 'Their programs, favourites and notes are removed from this phone. This cannot be undone.',
                      confirmLabel: 'Delete',
                      danger: true,
                    });
                    if (ok) store.deleteProfile(p.id);
                  }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>

          <hr className="divider" />

          <div className="row">
            <input
              type="text"
              placeholder="New profile name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newName.trim()) {
                  store.createProfile(newName);
                  setNewName('');
                  setManaging(false);
                }
              }}
              aria-label="New profile name"
            />
            <button
              className="btn small primary"
              disabled={!newName.trim()}
              onClick={() => {
                store.createProfile(newName);
                setNewName('');
                setManaging(false);
              }}
            >
              Add
            </button>
          </div>
          <div className="faint" style={{ marginTop: 8 }}>
            A new profile starts with its own copy of the full program library.
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
